import { AuthState, isAuthenticatedUser, isPendingOnboardingUser } from "./api/accessControl/AuthState.ts";
import { User } from "./api/accessControl/User.ts";
import { Navigate } from "react-router-dom";
import { OnboardingFormPage } from "./pages/onboarding/OnboardingFormPage.tsx";
import { InvitedSignupPage } from "./pages/onboarding/InvitedSignupPage.tsx";
import { Login } from "./pages/login/Login.tsx";
import SettingsPage from "./pages/app/shared/SettingsPage.tsx";
import { Role } from "./api/accessControl/Role.ts";
import DashboardAdmin from "./pages/app/admin/DashboardAdmin.tsx";
import BaseTransactions from "./pages/app/admin/pages/baseTransactions/BaseTransactions.tsx";
import TransactionSpecifics from "./pages/app/admin/pages/transactionSpecifics/TransactionSpecifics.tsx";
import Export from "./pages/app/admin/pages/Export.tsx";
import UsersListPage from "./pages/app/admin/pages/UsersListPage.tsx";
import ImportJobsPage from "./pages/app/admin/pages/ImportJobsPage.tsx";
import PartnerDashboard from "./pages/app/partner/pages/partnerDashboardPage/PartnerDashboard.tsx";
import { BackendApi } from "./api/api.ts";
import { CustomerBankAccountRepository } from "./api/customerBankAccounts/CustomerBankAccountRepository.ts";
import DashboardEntrepreneur from "./pages/app/entrepreneur/pages/DashboardPage/DashboardEntrepreneur.tsx";
import CashflowPage from "./pages/app/entrepreneur/pages/CashflowPage/CashflowPage.tsx";
import InsightsPage from "./pages/app/entrepreneur/pages/InsightsPage.tsx";
import { TransactionsPage } from "./pages/app/entrepreneur/pages/TransactionsPage/TransactionsPage.tsx";
import TaxesPage from "./pages/app/entrepreneur/pages/TaxesPage.tsx";
import BanksPage from "./pages/app/entrepreneur/pages/BanksPage/BanksPage.tsx";
import { BankConnectionCallBackPage } from "./pages/bankAccountConnectionFlow/BankConnectionCallBackPage.tsx";
import { ConnectABankPage } from "./pages/bankAccountConnectionFlow/ConnectABankPage.tsx";
import { SyncBankDataPage } from "./pages/bankAccountConnectionFlow/SyncBankDataPage.tsx";
import React from "react";
import { TFICRouter } from "./lib/TFICRouter.tsx";
import URLCallbackRegisterer from "./pages/URLCallbackRegisterer.tsx";
import { BalancePage } from "./pages/app/entrepreneur/pages/BalancePage/BalancePage.tsx";
import BusinessesOverviewPage from "./pages/app/admin/pages/businessesOverviewPage/BusinessesOverviewPage.tsx";
import DemoPartnerAdminPage from "./pages/app/admin/pages/DemoPartnerAdminPage.tsx";
import { ReconnectBankSuccessPage } from "./pages/bankAccountConnectionFlow/ReconnectBankSuccessPage.tsx";
import EntrepreneurSettingsPage
    from "./pages/app/entrepreneur/pages/entrepreneurSettingsPage/EntrepreneurSettingPage.tsx";
import SubscriptionsPage from "./pages/app/entrepreneur/pages/mySubscriptionsPage/SubscriptionsPage.tsx";
import ShareDocumentsPage from "./pages/app/entrepreneur/pages/ShareDocumentsPage/ShareDocumentsPage.tsx";
import DoublePaymentsPage from "./pages/app/entrepreneur/pages/doublePaymentsPage/DoublePaymentsPage.tsx";
import PasswordResetPage from "./pages/login/PasswordResetPage.tsx";
import PasswordResetSuccessPage from "./pages/login/PasswordResetSuccessPage.tsx";
import { WelcomePage } from "./pages/app/entrepreneur/pages/WelcomePage.tsx";
import MyBusinessesPage from "./pages/app/entrepreneur/pages/MyBusinessesPage/MyBusinessesPage.tsx";
import PartnerClientInsightsPage from "./pages/app/partner/pages/PartnerClientInsightsPage.tsx";
import PartnerClientSettingsPage from "./pages/app/partner/pages/partnerDashboardPage/PartnerClientSettingsPage.tsx";
import PartnerAddBusinessPage from "./pages/app/partner/pages/partnerAddBusinessPage/PartnerAddBusinessPage.tsx";
import PartnerImportBookkeepingPage from "./pages/app/partner/pages/partnerImportBookkeepingPage/PartnerImportBookkeepingPage.tsx";
import PartnerSettingsPage from "./pages/app/partner/pages/partnerSettingsPage/PartnerSettingsPage.tsx";
import {
    FinancialServiceIntegrationSuccessPage
} from "./pages/FinancialServiceIntegrationFlow/FinancialServiceIntegrationSuccessPage.tsx";
import MatchesPage from "./pages/app/partner/pages/PartnerMatchesPage/MatchesPage.tsx";
import PartnerUnmatchedTransactionsPage
    from "./pages/app/partner/pages/partnerUnmatchedTransactionsPage/PartnerUnmatchedTransactionsPage.tsx";
import PartnerMatchPage from "./pages/app/partner/pages/PartnerMatchPage/PartnerMatchPage.tsx";
import PartnerDocumentsPage from "./pages/app/partner/pages/PartnerClientDocuments/PartnerDocumentsPage.tsx";
import { PartnerArchivedDocumentsPage } from "./pages/app/partner/pages/PartnerArchivedDocuments/PartnerArchivedDocumentsPage.tsx";
import PartnerDocumentsToCheckPage from "./pages/app/partner/pages/PartnerDocumentsToCheck/PartnerDocumentsToCheckPage.tsx";
import { DocumentDrawerProvider } from "./views/DocumentDrawer";
import CreateInvoicePage from "./pages/app/entrepreneur/pages/CreateInvoicePage/CreateInvoicePage.tsx";
import CreditInvoicePage from "./pages/app/entrepreneur/pages/CreditInvoicePage/CreditInvoicePage.tsx";
import CustomersPage from "./pages/app/entrepreneur/pages/CustomersPage/CustomersPage.tsx";
import CatalogPage from "./pages/app/entrepreneur/pages/CatalogPage/CatalogPage.tsx";
import RecurringInvoicesPage from "./pages/app/entrepreneur/pages/RecurringInvoicesPage/RecurringInvoicesPage.tsx";
import OutstandingPurchaseInvoicesPage from "./pages/app/entrepreneur/pages/OutstandingPurchaseInvoicesPage/OutstandingPurchaseInvoicesPage.tsx";
import OutstandingSalesInvoicesPage from "./pages/app/entrepreneur/pages/OutstandingSalesInvoicesPage/OutstandingSalesInvoicesPage.tsx";
import SalesInvoiceDetailPage from "./pages/app/entrepreneur/pages/SalesInvoiceDetailPage/SalesInvoiceDetailPage.tsx";
import DraftInvoicesPage from "./pages/app/entrepreneur/pages/DraftInvoicesPage/DraftInvoicesPage.tsx";
import PartnerTransactionsPage from "./pages/app/partner/pages/partnerTransactionsPage/PartnerTransactionsPage.tsx";
import DocumentSearchPage from "./pages/app/ro_developer/DocumentSearchPage.tsx";
import FirebaseActionHandler from "./pages/firebaseActionHandlers/FirebaseActionHandler.tsx";
import { BeforeConnectABank } from "./pages/bankAccountConnectionFlow/BeforeConnectABank.tsx";
import { canUseInvoicing } from "./lib/invoicingAccess.ts";
import { canSeeReports } from "./lib/reportsAccess.ts";
import { ReportsPage } from "./pages/app/entrepreneur/pages/ReportsPage/ReportsPage.tsx";
import { VatDeclarationApprovalPage } from "./pages/app/entrepreneur/pages/VatDeclarationApprovalPage/VatDeclarationApprovalPage.tsx";
async function userHasConnectedAtLeastOneBankAccount(api: BackendApi, user: User | (User & { businesses: unknown })): Promise<boolean> {
    const customerBankAccountRepository = new CustomerBankAccountRepository(api)

    const userHasABusiness = 'businesses' in user && user.businesses.length > 0
    if (!userHasABusiness)
        return false

    const bankAccounts = await customerBankAccountRepository.getMyBankAccounts()
    return bankAccounts.length > 0
}

function invoicingRoute(user: User, page: React.ReactElement): React.ReactElement {
    return canUseInvoicing(user.businesses) ? page : <Navigate to="/" replace />
}

export async function resolveRoutesForAuthState(authState: AuthState) {

    const router = new TFICRouter(authState)

    router.registerCondition({ name: 'AlwaysVisible', conditionEval: () => true })

    router.registerRoute('/integrations/exact/oauth-callback', 'AlwaysVisible', (user) => <URLCallbackRegisterer />)
    router.registerRoute('/authentication/action', 'AlwaysVisible', (user) => <FirebaseActionHandler />)

    router.registerCondition({ name: 'UserNeedsOnboarding', conditionEval: (user) => isPendingOnboardingUser(user) })
    router.registerRoute('/onboarding', 'UserNeedsOnboarding', () => <OnboardingFormPage />)
    router.registerRoute('*', 'UserNeedsOnboarding', (user) => <Navigate to="/onboarding?sso=1" replace />)

    router.registerCondition({ name: 'UserIsNotLoggedIn', conditionEval: (user) => user === null })
    router.registerRoute('/onboarding', 'UserIsNotLoggedIn', (user) => <OnboardingFormPage />)
    router.registerRoute('/onboarding/invitation/:token', 'UserIsNotLoggedIn', (user) => <InvitedSignupPage />)
    router.registerRoute('/reset-password', 'UserIsNotLoggedIn', (user) => <PasswordResetPage />)
    router.registerRoute('/reset-password-success', 'UserIsNotLoggedIn', (user) => <PasswordResetSuccessPage />)
    router.registerRoute('*', 'UserIsNotLoggedIn', (user) => <Login />)


    router.registerCondition({ name: 'AnyRole', conditionEval: (user) => isAuthenticatedUser(user) })

    router.registerRoute('/financial-service-connection-success', 'AnyRole', (user) => <FinancialServiceIntegrationSuccessPage currentUser={user} />)

    router.registerCondition({ name: 'IsRO_Developer', conditionEval: (user) => isAuthenticatedUser(user) && user.role === Role.RO_Developer })
    router.registerRoute('*', 'IsRO_Developer', (user) => <DocumentSearchPage currentUser={user} />)

    router.registerCondition({ name: 'IsAdmin', conditionEval: (user) => isAuthenticatedUser(user) && user.role === Role.Admin })
    router.registerRoute('/my-profile', 'IsAdmin', (user) => <SettingsPage currentUser={user} />)
    router.registerRoute('/', 'IsAdmin', (user) => <DashboardAdmin currentUser={user} />)
    router.registerRoute('/base-transactions', 'IsAdmin', (user) => <BaseTransactions currentUser={user} />)
    router.registerRoute('/base-transactions/:transactionId', 'IsAdmin', (user) => <TransactionSpecifics
        currentUser={user} />)

    router.registerRoute('/export', 'IsAdmin', (user) => <Export currentUser={user} />)
    router.registerRoute('/users-list', 'IsAdmin', (user) => <UsersListPage currentUser={user} />)
    router.registerRoute('/businesses', 'IsAdmin', (user) => <BusinessesOverviewPage currentUser={user} />)
    router.registerRoute('/import-jobs', 'IsAdmin', (user) => <ImportJobsPage currentUser={user} />)
    router.registerRoute('/admin/demo-partner', 'IsAdmin', (user) => <DemoPartnerAdminPage currentUser={user} />)
    router.registerRoute('/document-search', 'IsAdmin', (user) => <DocumentSearchPage currentUser={user} />)

    router.registerCondition({ name: 'IsPartner', conditionEval: (user) => isAuthenticatedUser(user) && user.role === Role.Partner })
    router.registerRoute('/my-profile', 'IsPartner', (user) => <PartnerSettingsPage currentUser={user} />)
    router.registerRoute('/transactions', 'IsPartner', (user) => (
        <DocumentDrawerProvider currentUser={user}>
            <PartnerTransactionsPage currentUser={user} />
        </DocumentDrawerProvider>
    ))
    router.registerRoute('/unmatched-transactions', 'IsPartner', (user) => (
        <DocumentDrawerProvider currentUser={user}>
            <PartnerUnmatchedTransactionsPage currentUser={user} />
        </DocumentDrawerProvider>
    ))
    router.registerRoute('/matches', 'IsPartner', (user) => (
        <DocumentDrawerProvider currentUser={user}>
            <MatchesPage currentUser={user} />
        </DocumentDrawerProvider>
    ))
    router.registerRoute('/match/:matchId', 'IsPartner', (user) => <PartnerMatchPage currentUser={user} />)
    router.registerRoute('/documents-to-check/:documentId?', 'IsPartner', (user) => (
        <DocumentDrawerProvider currentUser={user}>
            <PartnerDocumentsToCheckPage currentUser={user} />
        </DocumentDrawerProvider>
    ))
    router.registerRoute('/documents/:documentId?', 'IsPartner', (user) => (
        <DocumentDrawerProvider currentUser={user}>
            <PartnerDocumentsPage currentUser={user} />
        </DocumentDrawerProvider>
    ))
    // TODO: route paths are hardcoded here, in Shell's nav and in each page's URL sync; move them to an app-wide route library so they can't drift.
    router.registerRoute('/archived-documents/:documentId?', 'IsPartner', (user) => (
        user.flags.getFlag('PARTNER__CAN_ARCHIVE_DOCUMENTS')
            ? (
                <DocumentDrawerProvider currentUser={user}>
                    <PartnerArchivedDocumentsPage currentUser={user} />
                </DocumentDrawerProvider>
            )
            : <Navigate to="/" replace />
    ))
    router.registerRoute('/add-business', 'IsPartner', (user) => <PartnerAddBusinessPage currentUser={user} />)
    router.registerRoute('/tools/opening-balances-import', 'IsPartner', (user) => <PartnerImportBookkeepingPage currentUser={user} />)
    router.registerRoute('*', 'IsPartner', (user) => <PartnerDashboard currentUser={user} />)
    router.registerRoute('/business/:businessId', 'IsPartner', (user) => (
        <DocumentDrawerProvider currentUser={user}>
            <PartnerClientSettingsPage currentUser={user} />
        </DocumentDrawerProvider>
    ))
    router.registerRoute('/client-insights/:businessId/:name', 'IsPartner', (user) => <PartnerClientInsightsPage currentUser={user} />)

    router.registerCondition({
        name: 'IsEntrepreneur',
        conditionEval: (user) => isAuthenticatedUser(user) && user.role === Role.Entrepreneur
    })

    router.registerRoute('/my-profile', 'IsEntrepreneur', (user) => <EntrepreneurSettingsPage currentUser={user} />)
    router.registerRoute('/vat-declaration-approval/:requestId', 'IsEntrepreneur', (user) => <VatDeclarationApprovalPage currentUser={user} />)
    router.registerCondition({
        name: 'IsEntrepreneurAndHasBankAccounts', conditionEval: async (user) => {
            const isEntrepreneur = isAuthenticatedUser(user) && user.role === Role.Entrepreneur
            if (!isEntrepreneur)
                return false

            const api = new BackendApi(user.getToken())
            return await userHasConnectedAtLeastOneBankAccount(api, user);
        }
    })

    router.registerRoute(
        '/',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <DashboardEntrepreneur currentUser={user} />
    )
    router.registerRoute(
        '/welcome',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <WelcomePage currentUser={user} />
    )
    router.registerRoute(
        '/cash-flow',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <CashflowPage currentUser={user} />
    )
    router.registerRoute(
        '/balance',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <BalancePage currentUser={user} />
    )
    router.registerRoute(
        '/insights',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <InsightsPage currentUser={user} />
    )
    router.registerRoute(
        '/transactions',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <TransactionsPage currentUser={user} />
    )
    router.registerRoute(
        '/taxes',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <TaxesPage currentUser={user} />
    )
    router.registerRoute(
        '/reports',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => canSeeReports(user) ? <ReportsPage currentUser={user} /> : <Navigate to="/" replace />
    )
    router.registerRoute(
        '/subscriptions',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <SubscriptionsPage currentUser={user} />
    )
    router.registerRoute(
        '/double-payments',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <DoublePaymentsPage currentUser={user} />
    )
    router.registerRoute(
        '/my-businesses',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <MyBusinessesPage currentUser={user} />
    )


    router.registerRoute(
        '/banks',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <BanksPage currentUser={user} />
    )

    router.registerRoute(
        '/share-documents',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => <ShareDocumentsPage currentUser={user} />
    )

    router.registerRoute(
        '/create-invoice',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <CreateInvoicePage currentUser={user} />)
    )
    router.registerRoute(
        '/credit-invoice',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <CreditInvoicePage currentUser={user} />)
    )
    router.registerRoute(
        '/customers',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <CustomersPage currentUser={user} />)
    )
    router.registerRoute(
        '/products-and-services',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <CatalogPage currentUser={user} />)
    )

    router.registerRoute(
        '/recurring-invoices',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <RecurringInvoicesPage currentUser={user} />)
    )
    router.registerRoute(
        '/outstanding-purchase-invoices',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <OutstandingPurchaseInvoicesPage currentUser={user} />)
    )
    router.registerRoute(
        '/outstanding-sales-invoices',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <OutstandingSalesInvoicesPage currentUser={user} />)
    )
    router.registerRoute(
        '/invoices/drafts',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <DraftInvoicesPage currentUser={user} />)
    )
    router.registerRoute(
        '/invoices/:invoiceId',
        'IsEntrepreneurAndHasBankAccounts',
        (user) => invoicingRoute(user, <SalesInvoiceDetailPage currentUser={user} />)
    )

    router.registerCondition({
        name: 'IsEntrepreneurAndDoesNotHaveBankAccounts',
        conditionEval: async (user) => {
            const isEntrepreneur = isAuthenticatedUser(user) && user.role === Role.Entrepreneur
            if (!isEntrepreneur)
                return false

            const api = new BackendApi(user.getToken())
            return !(await userHasConnectedAtLeastOneBankAccount(api, user));
        }
    })

    router.registerRoute('/documents/upload', 'IsEntrepreneur', () => <Navigate to="/share-documents" replace />)

    router.registerRoute(
        '/bank-connection-callback',
        'IsEntrepreneur',
        (user) => <BankConnectionCallBackPage currentUser={user} />
    )

    router.registerRoute(
        '/full-sync-bank-data',
        'IsEntrepreneur',
        (user) => <SyncBankDataPage currentUser={user} />
    )

    router.registerRoute(
        '/sync-bank-data-after-reconnect',
        'IsEntrepreneur',
        (user) => <ReconnectBankSuccessPage currentUser={user} />
    )

    router.registerRoute(
        '/connect-a-bank',
        'IsEntrepreneur',
        (user) => <ConnectABankPage currentUser={user} />
    )

    router.registerRoute(
        '*',
        'IsEntrepreneurAndDoesNotHaveBankAccounts',
        (user) => <BeforeConnectABank currentUser={user} />
    )

    return await router.resolve()

}
